import { FC, useState, useCallback } from 'react';
import { Typography, Stack, Paper, TextField, IconButton } from '@mui/material';
import { Edit, Check, Close } from '@mui/icons-material';

import { useStepEntryCrud } from '../hooks/useStepEntryCrud';

import { StepContent } from './StepContent';
import { StepResources } from './StepResources';


interface Props {
  step: Programs.ProgramStep;
  entry: Programs.StepEntry;
  canEdit?: boolean;
}

export const StepReview: FC<Props> = ({ step, entry, canEdit = false }) => {
  const [editing, setEditing] = useState(false);
  const [textResponse, setTextResponse] = useState(entry.textResponse);
  const { updateStepEntry } = useStepEntryCrud();

  const handleSave = useCallback(async () => {
    try {
      await updateStepEntry.mutateAsync({
        id: entry.id,
        textResponse,
      });
      setEditing(false);
    } catch (error) {
      console.error(error);
    }
  }, [updateStepEntry, entry.id, textResponse]);

  const handleCancel = useCallback(() => {
    setTextResponse(entry.textResponse);
    setEditing(false);
  }, [entry.textResponse]);

  return (
    <Stack spacing={3}>
      <StepContent step={step}/>

      {(entry.textResponse || editing) && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {step.promptTitle}
            </Typography>
            {canEdit && !editing && (
              <IconButton size="small" onClick={() => setEditing(true)} color="inherit">
                <Edit fontSize="small"/>
              </IconButton>
            )}
          </Stack>
          {editing ? (
            <Stack spacing={1}>
              <TextField
                multiline
                minRows={3}
                maxRows={8}
                value={textResponse}
                onChange={(e) => setTextResponse(e.target.value)}
                fullWidth
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <IconButton size="small" onClick={handleCancel} color="inherit">
                  <Close fontSize="small"/>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  color="primary"
                  disabled={updateStepEntry.isPending}
                >
                  <Check fontSize="small"/>
                </IconButton>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body1">
              {entry.textResponse}
            </Typography>
          )}
        </Paper>
      )}

      <StepResources resources={step.resources}/>
    </Stack>
  );
};
